import { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useCollections } from '@/src/modules/catalog/useCollections';
import { CatalogPricingProvider, useCatalogPricingMode } from '@/src/modules/catalog/useCatalogPricingMode';
import { isBackendOccasionType } from '@/src/modules/catalog/pendingSelection';
import { ProductCard } from './ProductCard';

const GRID_CLASSES = 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6';

const OCCASION_FILTERS = [
  { label: 'All', value: '' },
  { label: 'Weddings', value: 'WEDDING' },
  { label: 'Birthdays', value: 'BIRTHDAY' },
  { label: 'Anniversaries', value: 'ANNIVERSARY' },
  { label: 'Baby Showers', value: 'BABY_SHOWER' },
  { label: 'Send-offs', value: 'SEND_OFF' },
  { label: 'Engagements', value: 'ENGAGEMENT' },
  { label: 'Save the Date', value: 'SAVE_THE_DATE' },
  { label: 'Custom', value: 'CUSTOM' },
] as const;

function SkeletonGrid() {
  return (
    <div className={GRID_CLASSES}>
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-80 rounded-[2rem] bg-surface-low border border-outline-variant/40 p-5 flex flex-col justify-between animate-pulse"
        >
          <div className="w-full h-44 rounded-2xl bg-on-surface/10" />
          <div className="space-y-3 mt-4">
            <div className="h-4 w-3/4 rounded-full bg-on-surface/10" />
            <div className="h-3 w-1/2 rounded-full bg-on-surface/10" />
          </div>
        </div>
      ))}
    </div>
  );
}

function CatalogPageContent() {
  const [page, setPage] = useState(1);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const rawOccasion = searchParams.get('occasion') || searchParams.get('occasionType');
  const normalizedOccasion = rawOccasion ? rawOccasion.toUpperCase().replace(/-/g, '_') : '';
  const occasionType = isBackendOccasionType(normalizedOccasion) ? normalizedOccasion : undefined;

  // Normalise an invalid ?occasion= param out of the URL
  useEffect(() => {
    if (rawOccasion && !isBackendOccasionType(normalizedOccasion)) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('occasion');
      nextParams.delete('occasionType');
      setSearchParams(nextParams, { replace: true });
    }
  }, [rawOccasion, normalizedOccasion, searchParams, setSearchParams]);

  const isSearching = Boolean(searchQuery.trim());
  const effectivePage = isSearching ? 1 : page;
  // ponytail: search fetches one page of 50 and filters client-side. 50 is the backend's
  // pageSize ceiling (list-collections.request.dto.ts); the catalog has far fewer designs
  // today. If it ever exceeds 50, add a `q` param to GET /collections instead of paging here.
  const effectivePageSize = isSearching ? 50 : undefined;

  const { data, meta, loading, error, reload } = useCollections({
    page: effectivePage,
    pageSize: effectivePageSize,
    occasionType,
  });
  const { isPartnerUser, remainingCredits, walletBalance, balanceFormatted } = useCatalogPricingMode();

  const handleOccasionClick = (value: string) => {
    const nextParams = new URLSearchParams(searchParams);
    if (!value) {
      nextParams.delete('occasion');
      nextParams.delete('occasionType');
    } else {
      nextParams.set('occasion', value.toLowerCase().replace(/_/g, '-'));
      nextParams.delete('occasionType');
    }
    setSearchQuery('');
    setPage(1);
    setSearchParams(nextParams);
  };

  const filteredData = (data ?? []).filter((product) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    return (
      product.displayName.toLowerCase().includes(q) ||
      (product.description?.toLowerCase().includes(q) ?? false) ||
      product.code.toLowerCase().includes(q) ||
      (product.template?.name.toLowerCase().includes(q) ?? false)
    );
  });

  // If the backend ever clamps an out-of-range page (e.g. `?page=99` when
  // only 2 pages exist), fall back to whatever page it actually returned so
  // the Prev/Next buttons never disable against a page the server isn't on.
  //
  // Deliberately depends on `meta.page` alone, NOT `page` — `useCollections`
  // does not clear `meta` while a new page is loading (it only tracks
  // `loading`/`error`), so `meta` is briefly stale-behind-`page` on every
  // click. Adding `page` to these deps re-runs this effect on that stale
  // render and immediately reverts the click (verified: it turns the fetch
  // sequence page=1→2→1, so Next never advances past page 1 — a real
  // regression a prior version of this fix introduced and
  // `CatalogPage.test.tsx` didn't catch because it mocks `meta.page` to
  // already match the requested page, which never happens against the real
  // hook). Do not "fix" this eslint-disable without a test against the real
  // `useCollections` hook proving the fetch sequence is `[1, 2]`, not `[1, 2, 1]`.
  useEffect(() => {
    if (meta && meta.page !== page) setPage(meta.page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta?.page]);

  const location = useLocation();
  const initialBanner = (location.state as { catalogBanner?: string } | null)?.catalogBanner ?? null;
  const [banner, setBanner] = useState<string | null>(initialBanner);

  return (
    <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8">
      {banner && (
        <div className="mb-8 flex items-center justify-between gap-4 rounded-2xl border border-status-failed/20 bg-status-failed/10 px-5 py-3 text-sm font-semibold text-status-failed">
          <span>{banner}</span>
          <button type="button" onClick={() => setBanner(null)} aria-label="Dismiss">
            &times;
          </button>
        </div>
      )}

      {isPartnerUser && (
        <div
          data-testid="partner-catalog-banner"
          className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-2xl border border-primary/20 bg-primary/5 px-6 py-4"
        >
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-primary">Partner Wholesale Catalog</span>
            </div>
            <p className="mt-1 text-sm text-on-surface-variant">
              Every design is unlocked from your wallet balance.
            </p>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <span className="text-sm font-semibold text-on-surface">
              Balance: <strong className="text-primary font-bold">{walletBalance !== undefined && walletBalance > 0 ? `₹${walletBalance.toLocaleString('en-IN')}` : '₹0'}</strong>
            </span>
            <Link
              to="/dashboard/partner?tab=credits"
              className="text-xs font-bold text-primary hover:underline bg-primary/10 px-3 py-1.5 rounded-lg"
            >
              Buy Partner Pack
            </Link>
          </div>
        </div>
      )}

      <div className="mb-10 text-center">
        <h1 className="text-4xl font-bold text-on-surface">Choose your invitation</h1>
        <p className="mt-3 text-lg text-on-surface-variant">
          Every design, ready to personalize and publish.
        </p>
      </div>

      {/* Search and Filters */}
      <div className="mb-8 flex flex-col md:flex-row items-center justify-between gap-4">
        {/* Occasion Filter Chips */}
        <div className="flex flex-wrap gap-2 w-full md:w-auto" aria-label="Filter by occasion">
          {OCCASION_FILTERS.map((filter) => {
            const isSelected = (!filter.value && !occasionType) || occasionType === filter.value;
            return (
              <button
                key={filter.value || 'all'}
                type="button"
                aria-pressed={isSelected}
                onClick={() => handleOccasionClick(filter.value)}
                className={`min-h-[44px] inline-flex items-center rounded-full px-4 py-1.5 text-xs font-semibold transition-all ${
                  isSelected
                    ? 'bg-primary text-white shadow-sm'
                    : 'bg-surface-low border border-outline-variant/40 text-on-surface-variant hover:text-on-surface hover:border-primary/40'
                }`}
              >
                {filter.label}
              </button>
            );
          })}
        </div>

        {/* Client-side Search */}
        <div className="w-full md:w-72 relative">
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search designs..."
            aria-label="Search designs"
            className="w-full rounded-full border border-outline-variant/40 bg-surface-low pl-4 pr-10 py-2 text-sm text-on-surface placeholder:text-on-surface-variant/60 outline-none focus:border-primary focus:ring-1 focus:ring-primary [&::-webkit-search-cancel-button]:hidden"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-on-surface-variant hover:text-on-surface p-1"
              aria-label="Clear search"
            >
              &times;
            </button>
          )}
        </div>
      </div>

      {loading && <SkeletonGrid />}

      {!loading && error && (
        <div className="flex flex-col items-center gap-4 rounded-[2rem] border border-outline-variant/30 bg-surface p-12 text-center">
          <p className="text-sm font-medium text-status-failed">{error}</p>
          <button
            type="button"
            onClick={() => void reload()}
            className="text-sm font-semibold text-primary hover:underline"
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && data && data.length === 0 && (
        <div className="rounded-[2rem] border border-outline-variant/30 bg-surface p-12 text-center">
          <p className="text-lg font-semibold text-on-surface">Nothing available right now</p>
          <p className="mt-2 text-sm text-on-surface-variant">
            Check back soon — new invitation designs are added regularly.
          </p>
          {occasionType && (
            <button
              type="button"
              onClick={() => handleOccasionClick('')}
              className="mt-4 text-sm font-semibold text-primary hover:underline"
            >
              Clear filter
            </button>
          )}
        </div>
      )}

      {!loading && !error && data && data.length > 0 && filteredData.length === 0 && (
        <div className="rounded-[2rem] border border-outline-variant/30 bg-surface p-12 text-center">
          <p className="text-lg font-semibold text-on-surface">No matching designs found</p>
          <p className="mt-2 text-sm text-on-surface-variant">
            Try adjusting your search query or choosing a different occasion.
          </p>
          <div className="mt-4 flex items-center justify-center gap-4">
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="text-sm font-semibold text-primary hover:underline"
              >
                Clear search
              </button>
            )}
            {occasionType && (
              <button
                type="button"
                onClick={() => handleOccasionClick('')}
                className="text-sm font-semibold text-primary hover:underline"
              >
                Clear filter
              </button>
            )}
          </div>
        </div>
      )}

      {!loading && !error && filteredData.length > 0 && (
        <>
          <div className={GRID_CLASSES}>
            {filteredData.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
          {!isSearching && meta && meta.totalPages > 1 && (
            <div className="mt-10 flex items-center justify-center gap-4 text-sm text-on-surface-variant">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="rounded-lg border border-outline-variant/40 px-4 py-2 font-semibold disabled:opacity-40"
              >
                Previous
              </button>
              <span>
                Page {meta.page} of {meta.totalPages}
              </span>
              <button
                type="button"
                disabled={page >= meta.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-lg border border-outline-variant/40 px-4 py-2 font-semibold disabled:opacity-40"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function CatalogPage() {
  return (
    <CatalogPricingProvider>
      <CatalogPageContent />
    </CatalogPricingProvider>
  );
}

export default CatalogPage;
